import * as React from 'react';

import {
  announcementWriteErrorMessage,
  type AnnouncementDto,
} from '@/api/announcements-admin-client';
import {
  useCreateAnnouncement,
  useEligibleCampaigns,
  usePublishAnnouncementNow,
  useScheduleAnnouncement,
  useUpdateAnnouncementDraft,
} from '@/api/use-announcements-admin';
import { RichHtmlEditor } from '@/components/rich-html-editor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { announcementStateLabel } from '@/lib/announcement-labels';
import { Badge } from '@/components/ui/badge';
import { AnnouncementModificationWindow } from '@/routes/admin/communication/announcement-modification-window';
import { formatDateTime } from '@/routes/admin/communication/announcements-format';

// =============================================================================
// announcements-composer.tsx — Composer de diffusion globale par campagne (R15-COM-03, DBU §2.7.3).
// =============================================================================
// Formulaire : titre (DEC-35, obligatoire) + éditeur riche RÉUTILISÉ (RichHtmlEditor, le même WYSIWYG que
// les CGU §2.9 — ZÉRO nouveau moteur) pour `contentRich` (obligatoire) + sélecteur de campagne cible
// (campagnes éligibles, filtrage serveur). AUTOSAVE silencieux du brouillon (debounce) : crée le brouillon
// dès qu'il a de la substance (titre + contenu + campagne), puis PATCH les éditions suivantes.
//
// Planification : bascule envoi immédiat / envoi différé (date+heure). Immédiat → publish-now (→ Modifiable,
// fenêtre de modification) ; différé → schedule (→ Planifiee). Post-envoi immédiat : bandeau fenêtre de
// modification + édition inline (composant dédié). Les états terminaux (Planifiee/Verrouillee/Annulee) sont
// présentés en LECTURE SEULE. Le composant est REMONTÉ (key = sélection) à chaque changement de sélection.
//
// State in-memory React + pas de localStorage (RULE_REF_006). WCAG 2.1 AA : libellés associés, erreurs en
// role="alert", statut doublé d'un libellé. Anti-LLMism éditorial : aucun em-dash parenthétique dans les libellés.

/** Délai de debounce de l'autosave (ms). Coalesce la frappe rapide en une écriture. */
const AUTOSAVE_DEBOUNCE_MS = 700;

/** Mode d'envoi choisi au composer. */
type SendMode = 'immediate' | 'deferred';

export interface AnnouncementComposerProps {
  /** Annonce ouverte depuis le registre (édition), ou null pour une nouvelle annonce. */
  readonly announcement?: AnnouncementDto | null;
  /** Horloge injectable (fenêtre de modification déterministe en test). */
  readonly nowFactory?: () => Date;
}

export function AnnouncementComposer({
  announcement = null,
  nowFactory,
}: AnnouncementComposerProps): React.JSX.Element {
  // `current` = annonce en cours de composition (self-managed après le seed initial ; le parent remonte le
  // composant via `key` quand la sélection change, donc l'initialiseur suffit).
  const [current, setCurrent] = React.useState<AnnouncementDto | null>(
    announcement,
  );

  // Rendu selon l'état : fenêtre de modification (Modifiable) / lecture seule (terminaux) / éditeur.
  if (current !== null && current.state === 'Modifiable') {
    return (
      <div className="space-y-6">
        <ComposerHeader current={current} />
        <AnnouncementModificationWindow
          announcement={current}
          onEdited={setCurrent}
          {...(nowFactory ? { nowFactory } : {})}
        />
      </div>
    );
  }
  if (
    current !== null &&
    (current.state === 'Planifiee' ||
      current.state === 'Verrouillee' ||
      current.state === 'Annulee')
  ) {
    return (
      <div className="space-y-6">
        <ComposerHeader current={current} />
        <ReadOnlyAnnouncement current={current} />
      </div>
    );
  }

  // `nowFactory` ne concerne que la fenêtre de modification (rendue ci-dessus) : l'éditeur de brouillon
  // n'a pas de dépendance temporelle, on ne le lui transmet donc pas.
  void nowFactory;
  return <ComposerEditor current={current} onCurrentChange={setCurrent} />;
}

/** En-tête récapitulatif (titre + campagne + badge d'état). Le libellé porte l'information (WCAG). */
function ComposerHeader({
  current,
}: {
  readonly current: AnnouncementDto;
}): React.JSX.Element {
  const stateInfo = announcementStateLabel(current.state);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="space-y-1">
        <h2 className="text-lg font-medium">{current.titre}</h2>
        <p className="text-sm text-muted-foreground">
          Campagne cible : {current.campaignIntitule ?? current.campaignId}
        </p>
      </div>
      <Badge variant={stateInfo.variant} withDot={stateInfo.variant === 'refused'}>
        {stateInfo.label}
      </Badge>
    </div>
  );
}

/** Vue LECTURE SEULE d'une annonce terminale/planifiée (aucune édition possible). */
function ReadOnlyAnnouncement({
  current,
}: {
  readonly current: AnnouncementDto;
}): React.JSX.Element {
  return (
    <section aria-labelledby="ann-readonly-heading" className="space-y-3">
      <h3 id="ann-readonly-heading" className="sr-only">
        Détail de l'annonce
      </h3>
      {current.state === 'Planifiee' ? (
        <p role="status" className="text-sm text-muted-foreground">
          Cette annonce est planifiée pour le{' '}
          <strong>{formatDateTime(current.scheduledFor)}</strong>. Vous pouvez
          l'annuler depuis le registre tant qu'elle n'est pas partie.
        </p>
      ) : current.state === 'Verrouillee' ? (
        <p role="status" className="text-sm text-muted-foreground">
          Cette annonce a été diffusée le{' '}
          <strong>{formatDateTime(current.sentAt)}</strong> à{' '}
          {current.nombreDestinataires ?? 0} destinataire(s). La fenêtre de
          modification est écoulée : elle est verrouillée.
        </p>
      ) : (
        <p role="status" className="text-sm text-muted-foreground">
          Cette annonce a été annulée : elle ne sera pas diffusée.
        </p>
      )}
    </section>
  );
}

interface ComposerEditorProps {
  readonly current: AnnouncementDto | null;
  readonly onCurrentChange: (next: AnnouncementDto) => void;
}

/** Éditeur de brouillon (nouveau ou Brouillon existant) : titre + contenu + campagne + planification. */
function ComposerEditor({
  current,
  onCurrentChange,
}: ComposerEditorProps): React.JSX.Element {
  const eligible = useEligibleCampaigns();
  const createMut = useCreateAnnouncement();
  const updateMut = useUpdateAnnouncementDraft();
  const scheduleMut = useScheduleAnnouncement();
  const publishMut = usePublishAnnouncementNow();

  // Champs du formulaire (state in-memory ; le composant est remonté par le parent au changement de sélection).
  const [campaignId, setCampaignId] = React.useState(current?.campaignId ?? '');
  const [titre, setTitre] = React.useState(current?.titre ?? '');
  const [contentRich, setContentRich] = React.useState(current?.contentRich ?? '');
  const [sendMode, setSendMode] = React.useState<SendMode>('immediate');
  const [scheduledLocal, setScheduledLocal] = React.useState('');

  // Suit l'annonce persistée + le dernier snapshot écrit (skip des PATCH redondants).
  const draftRef = React.useRef<AnnouncementDto | null>(current);
  const lastPersisted = React.useRef<{ titre: string; contentRich: string } | null>(
    current !== null
      ? { titre: current.titre, contentRich: current.contentRich }
      : null,
  );
  const creatingRef = React.useRef(false);

  const titreEmpty = titre.trim() === '';
  const contentEmpty = contentRich.trim() === '';
  const canPersist = campaignId !== '' && !titreEmpty && !contentEmpty;

  const applyResult = React.useCallback(
    (result: AnnouncementDto): void => {
      draftRef.current = result;
      lastPersisted.current = {
        titre: result.titre,
        contentRich: result.contentRich,
      };
      onCurrentChange(result);
    },
    [onCurrentChange],
  );

  // AUTOSAVE silencieux (debounce) : crée le brouillon dès qu'il a de la substance, puis PATCH les éditions.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const draft = draftRef.current;
      if (draft === null) {
        if (creatingRef.current || !canPersist) {
          return;
        }
        creatingRef.current = true;
        createMut.mutate(
          { campaignId, titre, contentRich },
          {
            onSuccess: (created) => {
              creatingRef.current = false;
              applyResult(created);
            },
            onError: () => {
              creatingRef.current = false;
            },
          },
        );
        return;
      }
      if (draft.state !== 'Brouillon') {
        return;
      }
      const lp = lastPersisted.current;
      if (lp !== null && lp.titre === titre && lp.contentRich === contentRich) {
        return;
      }
      updateMut.mutate(
        { announcementId: draft.announcementId, patch: { titre, contentRich } },
        { onSuccess: (updated) => applyResult(updated) },
      );
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutations sont stables ; on redéclenche sur les champs.
  }, [titre, contentRich, campaignId, canPersist]);

  /** Garantit un brouillon persisté à jour (crée si besoin, flush les éditions non encore patchées). */
  const ensureFlushedDraft =
    React.useCallback(async (): Promise<AnnouncementDto | null> => {
      let draft = draftRef.current;
      if (draft === null) {
        if (!canPersist) {
          return null;
        }
        draft = await createMut.mutateAsync({ campaignId, titre, contentRich });
        applyResult(draft);
        return draft;
      }
      if (draft.state !== 'Brouillon') {
        return draft;
      }
      const lp = lastPersisted.current;
      if (lp === null || lp.titre !== titre || lp.contentRich !== contentRich) {
        draft = await updateMut.mutateAsync({
          announcementId: draft.announcementId,
          patch: { titre, contentRich },
        });
        applyResult(draft);
      }
      return draft;
    }, [canPersist, campaignId, titre, contentRich, createMut, updateMut, applyResult]);

  const onPublishNow = React.useCallback((): void => {
    void (async (): Promise<void> => {
      try {
        const draft = await ensureFlushedDraft();
        if (draft === null) {
          return;
        }
        const updated = await publishMut.mutateAsync(draft.announcementId);
        applyResult(updated);
      } catch {
        // L'erreur est portée par l'état de mutation (publishMut.isError) — rendu ci-dessous.
      }
    })();
  }, [ensureFlushedDraft, publishMut, applyResult]);

  const onSchedule = React.useCallback((): void => {
    void (async (): Promise<void> => {
      if (scheduledLocal === '') {
        return;
      }
      const scheduledFor = new Date(scheduledLocal).toISOString();
      try {
        const draft = await ensureFlushedDraft();
        if (draft === null) {
          return;
        }
        const updated = await scheduleMut.mutateAsync({
          announcementId: draft.announcementId,
          scheduledFor,
        });
        applyResult(updated);
      } catch {
        // Erreur portée par scheduleMut.isError.
      }
    })();
  }, [scheduledLocal, ensureFlushedDraft, scheduleMut, applyResult]);

  const campaignLocked = current !== null; // campagne figée une fois le brouillon ouvert/créé.
  const isBusy =
    createMut.isPending ||
    updateMut.isPending ||
    scheduleMut.isPending ||
    publishMut.isPending;
  const scheduleDisabled = !canPersist || scheduledLocal === '' || isBusy;
  const publishDisabled = !canPersist || isBusy;

  return (
    <div className="space-y-6">
      {/* Sélecteur de campagne cible (campagnes éligibles — filtrage serveur). */}
      <div className="space-y-2">
        <Label htmlFor="ann-campaign">Campagne cible</Label>
        {campaignLocked ? (
          <p id="ann-campaign" className="text-sm text-foreground">
            {current?.campaignIntitule ?? current?.campaignId}
          </p>
        ) : eligible.isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement des campagnes éligibles…
          </p>
        ) : eligible.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Erreur de chargement des campagnes : {eligible.error.message}
          </p>
        ) : (
          <select
            id="ann-campaign"
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">Sélectionnez une campagne…</option>
            {eligible.data.map((campaign) => (
              <option key={campaign.campaignId} value={campaign.campaignId}>
                {campaign.intitule}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Titre (DEC-35 : obligatoire). */}
      <div className="space-y-2">
        <Label htmlFor="ann-titre">Titre de l'annonce</Label>
        <Input
          id="ann-titre"
          value={titre}
          onChange={(event) => setTitre(event.target.value)}
          placeholder="Objet de l'annonce"
          aria-invalid={titreEmpty}
          aria-describedby="ann-titre-hint"
        />
        <p id="ann-titre-hint" className="text-xs text-muted-foreground">
          Le titre est obligatoire. Il figure dans le registre et le centre de
          notifications des fournisseurs.
        </p>
      </div>

      {/* Contenu riche RÉUTILISÉ (même WYSIWYG que les CGU). */}
      <RichHtmlEditor
        id="ann-content"
        label="Contenu de l'annonce"
        hint="Rédigez le message diffusé (titres, listes, gras, liens). Il est nettoyé et sécurisé à l'enregistrement."
        value={contentRich}
        onChange={setContentRich}
      />

      {/* Indicateur d'autosave discret (silencieux : muted, non intrusif, DBU §1.4.7). */}
      <p role="status" className="text-xs text-muted-foreground">
        {createMut.isPending || updateMut.isPending
          ? 'Enregistrement automatique du brouillon…'
          : draftRef.current !== null
            ? 'Brouillon enregistré automatiquement.'
            : 'Le brouillon sera enregistré automatiquement dès qu’il aura un titre et un contenu.'}
      </p>

      {createMut.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {announcementWriteErrorMessage(
            createMut.error,
            "L'enregistrement du brouillon a échoué.",
          )}
        </p>
      ) : null}

      {/* Planification : bascule envoi immédiat / différé. */}
      <fieldset className="space-y-3 rounded-lg border border-border p-4">
        <legend className="px-1 text-sm font-medium">Planification de l'envoi</legend>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="ann-send-mode"
              value="immediate"
              checked={sendMode === 'immediate'}
              onChange={() => setSendMode('immediate')}
              className="h-4 w-4"
            />
            Envoi immédiat
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="ann-send-mode"
              value="deferred"
              checked={sendMode === 'deferred'}
              onChange={() => setSendMode('deferred')}
              className="h-4 w-4"
            />
            Envoi différé
          </label>
        </div>

        {sendMode === 'deferred' ? (
          <div className="space-y-2">
            <Label htmlFor="ann-scheduled">Date et heure d'envoi</Label>
            <Input
              id="ann-scheduled"
              type="datetime-local"
              value={scheduledLocal}
              onChange={(event) => setScheduledLocal(event.target.value)}
            />
          </div>
        ) : null}

        {sendMode === 'immediate' ? (
          <Button type="button" onClick={onPublishNow} disabled={publishDisabled}>
            {publishMut.isPending ? 'Diffusion…' : 'Diffuser maintenant'}
          </Button>
        ) : (
          <Button type="button" onClick={onSchedule} disabled={scheduleDisabled}>
            {scheduleMut.isPending ? 'Planification…' : "Planifier l'envoi"}
          </Button>
        )}

        {!canPersist ? (
          <p className="text-xs text-muted-foreground">
            Renseignez la campagne, le titre et le contenu pour activer la diffusion.
          </p>
        ) : null}

        {publishMut.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {announcementWriteErrorMessage(
              publishMut.error,
              'La diffusion immédiate a échoué.',
            )}
          </p>
        ) : null}
        {scheduleMut.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {announcementWriteErrorMessage(
              scheduleMut.error,
              'La planification a échoué.',
            )}
          </p>
        ) : null}
      </fieldset>
    </div>
  );
}
